import { useTheme } from '@emotion/react';
import { useSetAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { useRouter } from 'next/router';
import React, { useCallback } from 'react';

import { Flex, Icon, Text } from '~/components/blocks';
import { NotificationListItem } from '~/components/partials/NotificationListItem';
import { OrganizationEntryNotificationFragment } from '~/graphql/generated/graphql';
import { notificationPopoverState } from '~/state/layouts/SharedAppShell/atoms';
import { profileDrawerState } from '~/state/partials/patient_profile_drawer/atoms';
import {
  checkinEntryProfileDrawerState,
  draftAppointmentPatientProfileDrawerState,
  lineVisitorProfileDrawerState,
  outpatientQuestionnaireEntryProfileDrawerState,
  webVisitorProfileDrawerState,
} from '~/state/reception/atoms';
import { Label } from '~/utils/label';

type Props = {
  entryNotification: OrganizationEntryNotificationFragment;
};

export const EntryNotification = (props: Props) => {
  const { entryNotification } = props;
  const router = useRouter();
  const theme = useTheme();
  const setCheckinEntryProfileDrawerState = useSetAtom(checkinEntryProfileDrawerState);
  const setOutpatientQuestionnaireEntryProfileDrawerState = useSetAtom(
    outpatientQuestionnaireEntryProfileDrawerState,
  );
  const resetCheckinEntryProfileDrawerState = useResetAtom(checkinEntryProfileDrawerState);
  const resetOutpatientQuestionnaireEntryProfileDrawerState = useResetAtom(
    outpatientQuestionnaireEntryProfileDrawerState,
  );
  const resetProfileDrawerState = useResetAtom(profileDrawerState);
  const resetLineVisitorProfileDrawerState = useResetAtom(lineVisitorProfileDrawerState);
  const resetWebVisitorProfileDrawerState = useResetAtom(webVisitorProfileDrawerState);
  const resetDraftAppointmentPatientProfileDrawerState = useResetAtom(
    draftAppointmentPatientProfileDrawerState,
  );

  const resetNotificationPopoverState = useResetAtom(notificationPopoverState);
  const handleOpenReception = useCallback(
    async (e: React.MouseEvent<HTMLLIElement>) => {
      // 受付ドロアーで既読処理をするため、親要素で既読処理が実行されないようにする
      e.stopPropagation();

      if (router.pathname !== '/reception') {
        await router.push('/reception');
      }

      // 受付通知パネルを閉じる
      resetNotificationPopoverState();

      if (entryNotification.checkinEntry) {
        setCheckinEntryProfileDrawerState({
          isOpen: true,
          checkinEntryId: entryNotification.checkinEntry.id,
        });
        resetOutpatientQuestionnaireEntryProfileDrawerState();
        resetProfileDrawerState();
        resetLineVisitorProfileDrawerState();
        resetWebVisitorProfileDrawerState();
        resetDraftAppointmentPatientProfileDrawerState();
      }

      if (entryNotification.outpatientQuestionnaireEntry) {
        setOutpatientQuestionnaireEntryProfileDrawerState({
          isOpen: true,
          outpatientQuestionnaireEntryId: entryNotification.outpatientQuestionnaireEntry.id,
        });
        resetCheckinEntryProfileDrawerState();
        resetProfileDrawerState();
        resetLineVisitorProfileDrawerState();
        resetWebVisitorProfileDrawerState();
        resetDraftAppointmentPatientProfileDrawerState();
      }
    },
    [
      entryNotification,
      router,
      setCheckinEntryProfileDrawerState,
      setOutpatientQuestionnaireEntryProfileDrawerState,
      resetNotificationPopoverState,
      resetCheckinEntryProfileDrawerState,
      resetOutpatientQuestionnaireEntryProfileDrawerState,
      resetProfileDrawerState,
      resetLineVisitorProfileDrawerState,
      resetWebVisitorProfileDrawerState,
      resetDraftAppointmentPatientProfileDrawerState,
    ],
  );
  const isTaskUncompleted = !entryNotification.isRead;

  return (
    <NotificationListItem onClick={handleOpenReception} isMarked={isTaskUncompleted}>
      <Flex flexDirection="column" marginRight={theme.space.m}>
        <Text fontWeight="bold" size="s">
          {entryNotification.subject}
        </Text>
        <Text size="xs" color="grey01" marginTop={theme.space.xs}>
          {`${Label.YYYYMMDDja(entryNotification.updatedAt)} ${Label.HHMM(entryNotification.updatedAt)}`}
        </Text>
      </Flex>
      <Icon icon="forward-line" size="m" marginLeft="auto" />
    </NotificationListItem>
  );
};
